import logoAvif from '../assets/optimized/bestbeighbor2.avif';
import { useState, useEffect, useRef } from 'react';
import { Bars3Icon, XMarkIcon, PhoneIcon, StarIcon, MapPinIcon, CalendarDaysIcon } from '@heroicons/react/24/solid';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/optimized/bestbeighbor2.webp';
import { useContent } from '../context/ContentContext';
import { GOOGLE_RATING } from '../lib/defaultContent';

const navItems = [
  { label: 'Home', href: '/', isRoute: true },
  { label: 'Membership / Plans', href: '/plans', isRoute: true },
  { label: 'Financing', href: '/financing', isRoute: true },
  { label: 'Service Location', href: '/service-areas', isRoute: true },
  { label: 'Contact Us', href: '/contact', isRoute: true },
];

const serviceLocations = [
  'McKinney, TX',
  'Frisco, TX',
  'Sherman, TX',
  'Allen, TX',
];

function NavLink({ item, className, onClick }: { item: typeof navItems[number]; className: string; onClick?: () => void }) {
  if (item.isRoute) {
    return (
      <Link to={item.href} className={className} onClick={onClick}>
        {item.label}
      </Link>
    );
  }
  return (
    <a href={item.href} className={className} onClick={onClick}>
      {item.label}
    </a>
  );
}

function CompactHeader({ visible, onMenuToggle, isMenuOpen, bookingUrl, phone }: { visible: boolean; onMenuToggle: () => void; isMenuOpen: boolean; bookingUrl: string; phone: string }) {
  const location = useLocation();
  const phoneDigits = phone.replace(/[^0-9]/g, '');

  return (
    <div
      className="hidden xl:block fixed top-0 left-0 right-0 z-[60] will-change-transform"
      style={{
        transform: visible ? 'translateY(0)' : 'translateY(-100%)',
        transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        pointerEvents: visible ? 'auto' : 'none',
        visibility: visible ? 'visible' : 'hidden',
      }}
    >
      <div className="bg-white shadow-lg border-b border-gray-100/80">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[76px] sm:h-[84px]">
            <Link to="/" className="flex-shrink-0">
              <picture><source type="image/avif" srcSet={logoAvif} /><img decoding="async" width={600} height={173}
                src={logo}
                alt="Best Neighbor Home Services"
                className="h-11 sm:h-12 xl:h-12 2xl:h-[52px] w-auto"
              /></picture>
            </Link>

            <div className="flex items-center gap-2.5 xl:hidden">
              <a
                href={`tel:${phoneDigits}`}
                className="flex items-center justify-center w-11 h-11 bg-brand-navy rounded-lg text-white"
                aria-label="Call us"
              >
                <PhoneIcon className="w-5 h-5" />
              </a>
              <a
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 bg-brand-orange text-white px-5 py-3 rounded-lg text-sm font-semibold hover:bg-brand-orange/90 transition-colors"
              >
                <CalendarDaysIcon className="w-4 h-4" />
                Book Now
              </a>
              <button
                onClick={onMenuToggle}
                className="flex items-center justify-center w-10 h-10 text-brand-navy"
                aria-label="Toggle menu"
                aria-expanded={isMenuOpen}
              >
                {isMenuOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
              </button>
            </div>

            <div className="hidden xl:flex items-center flex-1">
              <nav className="flex items-center ml-[30px]">
                <div className="flex items-center">
                  {navItems.map((item, index) => {
                    const isActive = item.isRoute && location.pathname === item.href;
                    return (
                      <div key={item.label} className="flex items-center">
                        {index > 0 && (
                          <span className="text-gray-300 mx-2.5 2xl:mx-3 text-lg font-light select-none">/</span>
                        )}
                        <NavLink
                          item={item}
                          className={`font-semibold text-sm 2xl:text-[15px] whitespace-nowrap transition-colors duration-200 ${
                            isActive
                              ? 'text-brand-orange'
                              : 'text-brand-navy hover:text-brand-orange'
                          }`}
                        />
                      </div>
                    );
                  })}
                </div>
              </nav>

              <div className="flex items-center gap-3 flex-shrink-0 ml-auto">
                <a
                  href={`tel:${phoneDigits}`}
                  className="flex items-center justify-center w-11 h-11 bg-brand-navy rounded-lg text-white hover:bg-brand-navy/90 transition-colors"
                  aria-label="Call us"
                >
                  <PhoneIcon className="w-5 h-5" />
                </a>
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-brand-orange text-white px-6 py-3 rounded-lg font-semibold text-[15px] hover:bg-brand-orange/90 transition-colors duration-200 whitespace-nowrap"
                >
                  <CalendarDaysIcon className="w-[18px] h-[18px]" />
                  Book Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Header() {
  const { settings } = useContent();
  const phoneDigits = settings.phone.replace(/[^0-9]/g, '');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showCompact, setShowCompact] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLElement>(null);
  const location = useLocation();

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const headerHeight = headerRef.current?.offsetHeight ?? 150;
          setShowCompact(window.scrollY > headerHeight - 20);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setHeaderHeight(el.offsetHeight);
    });
    ro.observe(el);
    setHeaderHeight(el.offsetHeight);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  return (
    <>
      <CompactHeader
        visible={showCompact}
        onMenuToggle={() => setIsMenuOpen(!isMenuOpen)}
        isMenuOpen={isMenuOpen}
        bookingUrl={settings.bookingUrl}
        phone={settings.phone}
      />

      <header ref={headerRef} className="fixed top-0 left-0 right-0 z-50 xl:relative">
        <div className="bg-brand-navy relative" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
          <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between py-2 xl:py-3">
              <div className="hidden md:flex items-center gap-2 text-white">
                <div className="flex items-center" style={{ gap: '0px' }}>
                  {[...Array(5)].map((_, i) => (
                    <StarIcon key={i} className="w-4 h-4 text-brand-orange" />
                  ))}
                </div>
                <span className="text-sm font-medium">{GOOGLE_RATING} Rating on Google</span>
              </div>

              <div className="flex md:hidden items-center gap-1.5 text-white">
                <StarIcon className="w-3.5 h-3.5 text-brand-orange" />
                <span className="text-xs font-medium">{GOOGLE_RATING} on Google</span>
                <span className="text-white/30 mx-1.5">|</span>
                <span className="text-xs text-white/80">24/7 Service</span>
                <span className="text-white/30 mx-1.5">|</span>
                <Link to="/service-areas" className="flex items-center gap-1 text-xs text-white/80 hover:text-white transition-colors">
                  <MapPinIcon className="w-3 h-3 text-brand-orange" />
                  <span>Service Location</span>
                </Link>
              </div>

              <div className="hidden xl:flex items-center gap-6">
                {serviceLocations.map((loc) => (
                  <div key={loc} className="flex items-center gap-1 text-white/90 text-sm">
                    <MapPinIcon className="w-3 h-3 text-brand-orange" />
                    <span>{loc}</span>
                  </div>
                ))}
              </div>

              <div className="hidden sm:flex items-center gap-3 md:gap-4">
                <span className="text-white/90 text-sm lg:hidden xl:inline">24/7 Service Available</span>
                <Link
                  to="/financing"
                  className="bg-brand-orange text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-brand-tan transition-colors duration-200"
                >
                  Financing
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow-md">
          <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between py-3 xl:flex-col xl:items-center xl:pt-4 xl:pb-3 2xl:pt-6 2xl:pb-4">
              <Link to="/" className="flex-shrink-0">
                <picture><source type="image/avif" srcSet={logoAvif} /><img decoding="async" width={600} height={173} src={logo} alt="Best Neighbor Home Services" className="h-12 sm:h-14 xl:h-14 2xl:h-20 w-auto xl:mb-4 2xl:mb-6" /></picture>
              </Link>

              <div className="flex items-center gap-[9px] xl:hidden">
                <a
                  href={`tel:${phoneDigits}`}
                  className="flex items-center justify-center w-11 h-11 text-brand-navy"
                  aria-label="Call us"
                >
                  <PhoneIcon className="w-[22px] h-[22px]" />
                </a>
                <span className="w-px h-5 bg-gray-300" />
                <Link
                  to="/contact"
                  className="flex items-center justify-center w-11 h-11 text-brand-navy"
                  aria-label="Book a service"
                >
                  <CalendarDaysIcon className="w-[22px] h-[22px]" />
                </Link>
                <span className="w-px h-5 bg-gray-300" />
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="flex items-center justify-center w-11 h-11 text-brand-navy"
                  aria-label="Toggle menu"
                aria-expanded={isMenuOpen}
                >
                  {isMenuOpen ? <XMarkIcon className="w-[26px] h-[26px]" /> : <Bars3Icon className="w-[26px] h-[26px]" />}
                </button>
              </div>

              <div className="hidden xl:flex items-center justify-between w-full xl:pt-2">
                <div className="flex items-center gap-2">
                  <a
                    href={`tel:${phoneDigits}`}
                    className="flex items-center gap-2 text-brand-navy font-semibold hover:text-brand-orange transition-colors lg:text-[15px] xl:text-[15px] 2xl:text-[18px]"
                  >
                    <PhoneIcon className="w-5 h-5 text-brand-orange" />
                    {settings.phone}
                  </a>
                </div>

                <nav className="flex items-center justify-center flex-1">
                  <div className="flex items-center">
                    {navItems.map((item, index) => {
                      const isActive = item.isRoute && location.pathname === item.href;
                      return (
                        <div key={item.label} className="flex items-center">
                          {index > 0 && (
                            <span className="text-gray-300 lg:mx-3 xl:mx-3 2xl:mx-5 text-lg font-light select-none">/</span>
                          )}
                          <NavLink
                            item={item}
                            className={`font-semibold transition-colors duration-200 lg:text-sm xl:text-sm 2xl:text-base ${
                              isActive
                                ? 'text-brand-orange'
                                : 'text-brand-navy hover:text-brand-orange'
                            }`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </nav>

                <div>
                  <a
                    href={settings.bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-brand-orange text-white lg:px-4 lg:py-2.5 xl:px-5 xl:py-2.5 2xl:px-6 2xl:py-3 rounded-lg font-semibold hover:bg-brand-navy transition-colors duration-200 lg:text-sm xl:text-sm 2xl:text-base"
                  >
                    <CalendarDaysIcon className="w-5 h-5" />
                    Book Your Service
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="header-spacer xl:hidden" style={{ height: headerHeight || undefined }} />

      <div
        className={`xl:hidden fixed inset-0 top-0 z-[70] transition-all duration-300 ${
          isMenuOpen ? 'visible' : 'invisible pointer-events-none'
        }`}
      >
        <div
          className={`absolute inset-0 bg-brand-navy/60 backdrop-blur-sm transition-opacity duration-300 ${
            isMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setIsMenuOpen(false)}
        />

        <div
          className={`absolute top-0 right-0 w-[85%] max-w-sm h-full bg-white shadow-2xl transition-transform duration-300 ease-out flex flex-col ${
            isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between p-5 border-b border-gray-100">
            <picture><source type="image/avif" srcSet={logoAvif} /><img decoding="async" width={600} height={173} src={logo} alt="Best Neighbor" className="h-10 w-auto" /></picture>
            <button
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center justify-center w-10 h-10 rounded-lg bg-brand-off-white text-brand-navy"
              aria-label="Close menu"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-5 py-6">
            <div className="space-y-1">
              {navItems.map((item) => {
                const isActive = item.isRoute && location.pathname === item.href;
                return (
                  <NavLink
                    key={item.label}
                    item={item}
                    className={`flex items-center px-4 py-3.5 rounded-xl font-medium text-base transition-colors ${
                      isActive
                        ? 'text-brand-orange bg-brand-orange/5'
                        : 'text-brand-navy hover:bg-brand-off-white'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  />
                );
              })}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-100">
              <a
                href={`tel:${phoneDigits}`}
                className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-brand-navy font-semibold text-lg"
              >
                <div className="w-10 h-10 bg-brand-orange/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <PhoneIcon className="w-5 h-5 text-brand-orange" />
                </div>
                {settings.phone}
              </a>
            </div>
          </nav>

          <div className="p-5 border-t border-gray-100 space-y-3 bg-brand-off-white/50">
            <a
              href={settings.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2.5 w-full bg-brand-orange text-white px-6 py-4 rounded-xl font-semibold text-base shadow-lg shadow-brand-orange/20"
              onClick={() => setIsMenuOpen(false)}
            >
              <CalendarDaysIcon className="w-5 h-5" />
              Book Your Service
            </a>
            <Link
              to="/financing"
              className="flex items-center justify-center w-full bg-brand-navy text-white px-6 py-3.5 rounded-xl font-semibold text-sm"
              onClick={() => setIsMenuOpen(false)}
            >
              Explore Financing Options
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
